/**
 * PDP upsell custom element.
 *
 * Drives the `<pdp-upsell>` block: reads server-rendered accessory
 * compatibility data, picks the top in-stock matches for the current
 * shoe variant (capped by MAX_UPSELLS), and reconciles the visible
 * slides inside <swiper-container> against a hidden pool sibling.
 *
 * Each accessory uses its first available variant for add-to-cart.
 *
 * Pairs with `blocks/pdp-upsell.liquid`, `snippets/pdp-upsell-card.liquid`,
 * and `assets/pdp-upsell.css`.
 */
const MAX_UPSELLS = 5;

if (!customElements.get('pdp-upsell')) {
  class PdpUpsell extends HTMLElement {
    constructor() {
      super();
      this._unsubscribeVariantChange = null;
    }

    connectedCallback() {
      this.productId = parseInt(this.dataset.productId, 10);
      this.styleOptionPosition = parseInt(this.dataset.styleOptionPosition || '0', 10);
      this.styleOptionName = (this.dataset.styleOptionName || '').toLowerCase();

      const datasetMax = parseInt(this.dataset.maxUpsells || '', 10);
      this.maxUpsells = Number.isFinite(datasetMax) && datasetMax > 0 ? datasetMax : MAX_UPSELLS;

      const configEl = this.querySelector('[data-pdp-upsell-config]');
      try {
        this.accessories = configEl ? JSON.parse(configEl.textContent) : [];
      } catch (err) {
        console.warn('PdpUpsell: failed to parse config', err);
        this.accessories = [];
      }

      this.swiperEl = this.querySelector('[data-pdp-upsell-swiper]');
      this.poolEl = this.querySelector('[data-pdp-upsell-pool]');

      this._reflectVisibleCount();

      if (typeof eventBus !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubscribeVariantChange = eventBus.subscribe(
          PUB_SUB_EVENTS.variantChange,
          this._onVariantChange.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (typeof this._unsubscribeVariantChange === 'function') {
        this._unsubscribeVariantChange();
      }
    }

    _onVariantChange(eventData) {
      const data = eventData && eventData.data ? eventData.data : {};
      const variant = data.variant;
      const container = data.container;
      if (!variant) return;

      if (!this._isEventForCurrentProduct(container, variant)) return;

      const styleValue = this._extractStyleFromVariant(variant);
      const newIndices = this._findBestAccessoryIndices(styleValue);
      this._reconcileSlides(newIndices);
    }

    _isEventForCurrentProduct(container, variant) {
      if (variant && variant.product_id && variant.product_id !== this.productId) {
        return false;
      }
      if (!container) return true;

      const rawId =
        (container.dataset && (container.dataset.product || container.dataset.productId)) ||
        container.getAttribute('data-product') ||
        null;
      const containerProductId = rawId ? parseInt(rawId, 10) : null;
      if (containerProductId && containerProductId !== this.productId) {
        return false;
      }

      const containerForm = container.closest && container.closest('product-form');
      if (containerForm && containerForm.dataset.format !== 'product-page') {
        return false;
      }
      return true;
    }

    _extractStyleFromVariant(variant) {
      if (this.styleOptionPosition >= 1 && this.styleOptionPosition <= 3) {
        return variant['option' + this.styleOptionPosition] || null;
      }
      if (Array.isArray(variant.options) && this.styleOptionPosition >= 1) {
        return variant.options[this.styleOptionPosition - 1] || null;
      }
      return null;
    }

    /**
     * Returns up to `this.maxUpsells` accessory indices matching the
     * given shoe style value, sorted by ascending sortPriority then
     * by collection order as a tiebreaker.
     *
     * `compatible_products` and `compatible_variants` are additive merchandising scopes.
     * An accessory is eligible when either the product or style scope matches; do not
     * require both unless the merchandising model changes.
     */
    _findBestAccessoryIndices(styleValue) {
      const candidates = [];

      for (let i = 0; i < this.accessories.length; i++) {
        const acc = this.accessories[i];
        if (!acc || !acc.available) continue;

        const productMatch =
          Array.isArray(acc.compatibleProductIds) &&
          acc.compatibleProductIds.indexOf(this.productId) !== -1;

        const styleMatch =
          styleValue &&
          Array.isArray(acc.compatibleStyles) &&
          acc.compatibleStyles.indexOf(styleValue) !== -1;

        if (!productMatch && !styleMatch) continue;

        const priority = typeof acc.sortPriority === 'number' ? acc.sortPriority : 9999;
        candidates.push({ idx: acc.idx, priority });
      }

      candidates.sort((a, b) => a.priority - b.priority || a.idx - b.idx);
      return candidates.slice(0, this.maxUpsells).map((c) => c.idx);
    }

    /**
     * Reconcile the live slides inside <swiper-container> against the
     * desired index list. Slides that should no longer be visible move
     * back to the pool; required slides are pulled from the pool. After
     * moving, slides are re-appended in the desired (priority) order
     * and Swiper is told to recompute.
     */
    _reconcileSlides(indices) {
      if (!this.swiperEl || !this.poolEl) return;

      const desired = new Set(indices);

      Array.from(this.swiperEl.querySelectorAll('[data-accessory-idx]')).forEach((slide) => {
        const idx = parseInt(slide.dataset.accessoryIdx, 10);
        if (!desired.has(idx)) {
          this.poolEl.appendChild(slide);
        }
      });

      indices.forEach((idx) => {
        const slide =
          this.swiperEl.querySelector('[data-accessory-idx="' + idx + '"]') ||
          this.poolEl.querySelector('[data-accessory-idx="' + idx + '"]');
        if (slide) {
          this.swiperEl.appendChild(slide);
        }
      });

      this._reflectVisibleCount();
      this._refreshSwiper(true);
    }

    /**
     * Adds `pdp-upsell--single` when only one slide is in the swiper,
     * `pdp-upsell--hidden` when zero. CSS uses these to hide the
     * scrollbar and make the lone card span the full width.
     */
    _reflectVisibleCount() {
      const visibleCount = this.swiperEl
        ? this.swiperEl.querySelectorAll('[data-accessory-idx]').length
        : 0;

      this.classList.toggle('pdp-upsell--hidden', visibleCount === 0);
      this.classList.toggle('pdp-upsell--single', visibleCount === 1);
    }

    /**
     * Wait for swiper-element to register and instantiate, then call
     * update(). Resets scroll position when `resetScroll` is true.
     */
    _refreshSwiper(resetScroll) {
      if (!this.swiperEl) return;

      const apply = () => {
        const sw = this.swiperEl.swiper;
        if (!sw) return;
        sw.update();
        if (resetScroll && typeof sw.slideTo === 'function') {
          sw.slideTo(0, 0);
        }
      };

      if (this.swiperEl.swiper) {
        apply();
        return;
      }

      let attempts = 0;
      const tryLater = () => {
        if (this.swiperEl.swiper) {
          apply();
          return;
        }
        attempts += 1;
        if (attempts > 30) return;
        requestAnimationFrame(tryLater);
      };
      tryLater();
    }
  }

  customElements.define('pdp-upsell', PdpUpsell);
}
